import { useForm, zodResolver } from "@mantine/form";
import {
  Button,
  DateInput,
  Grid,
  Option,
  TextAreaInput,
  TextInput,
  styled,
} from "@monolith-forensics/monolith-ui";
import {
  PreservationStatusSelectBox,
  PreservationMethodSelectBox,
  PreservationTriggerTypeSelectBox,
} from "../Selectors";
import { EvidenceSelector } from "@/Features/Evidence";
import { ContactSelector } from "@/Features/Contacts";
import { DateInputFormat, getDateFormat } from "@/utils/date-format";
import { DefaultPreservationSchema } from "../preservations.schema";

export type DefaultPreservationFormData = {
  case_id: number;
  name: string;
  trigger_date?: string | null;
  trigger_type_id?: number | null;
  method_id?: number | null;
  status_id?: number | null;
  contact_id?: number | null;
  linked_contact?: Option | null;
  evidence_id?: number | null;
  linked_evidence?: Option | null;
  acquisition_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  description?: string | null;
};

export type DefaultPreservationFormProps = {
  title: string;
  isPending?: boolean;
  onCancel?: () => void;
  onSubmit?: (
    formData: DefaultPreservationFormData,
    diffData: Partial<DefaultPreservationFormData>
  ) => void;
  defaultFormData?: DefaultPreservationFormData;
};

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 550px;
`;

const ButtonMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

export const DefaultPreservationForm: React.FC<DefaultPreservationFormProps> =
  ({ title, isPending, onCancel, onSubmit, defaultFormData }) => {
    const form = useForm<DefaultPreservationFormData>({
      initialValues: {
        case_id: defaultFormData?.case_id || 0,
        name: defaultFormData?.name || "",
        trigger_date: defaultFormData?.trigger_date || null,
        trigger_type_id: defaultFormData?.trigger_type_id || null,
        method_id: defaultFormData?.method_id || null,
        status_id: defaultFormData?.status_id || null,
        contact_id: defaultFormData?.contact_id || null,
        linked_contact: defaultFormData?.linked_contact || null,
        evidence_id: defaultFormData?.evidence_id || null,
        linked_evidence: defaultFormData?.linked_evidence || null,
        acquisition_id: defaultFormData?.acquisition_id || null,
        start_date: defaultFormData?.start_date || null,
        end_date: defaultFormData?.end_date || null,
        description: defaultFormData?.description || "",
      },
      validate: zodResolver(DefaultPreservationSchema),
    });

    const handleCancel = () => {
      onCancel?.();
    };

    const handleSubmit = () => {
      const validateResult = form.validate();
      if (validateResult.hasErrors) return;

      const formData = form.getValues();
      const parsed = DefaultPreservationSchema.parse(formData);
      const dirty = form.getDirty();

      const schemaKeys = Object.keys(
        DefaultPreservationSchema.shape
      ) as (keyof typeof parsed)[];
      const diffData = schemaKeys.reduce((acc, field) => {
        if (dirty[field]) acc[field] = parsed[field];
        return acc;
      }, {} as Record<string, unknown>);

      onSubmit?.(parsed, diffData);
    };

    return (
      <FormContainer>
        <FormTitle>{title}</FormTitle>
        <Grid col={2}>
          <TextInput
            size="sm"
            variant="outlined"
            label="Name"
            placeholder="Enter preservation name"
            description="The name of the preservation"
            required={true}
            colSpan={2}
            key={form.key("name")}
            {...form.getInputProps("name")}
          />
          <PreservationTriggerTypeSelectBox
            key={form.key("trigger_type_id")}
            {...form.getInputProps("trigger_type_id")}
          />
          <DateInput
            size="sm"
            variant="outlined"
            label="Trigger Date"
            description="Date when preservation trigger occurred"
            clearable={true}
            arrow={true}
            enableCalendar={true}
            format={
              getDateFormat({
                isMoment: true,
                includeTime: false,
              }) as DateInputFormat
            }
            key={form.key("trigger_date")}
            {...form.getInputProps("trigger_date")}
          />
          <PreservationMethodSelectBox
            key={form.key("method_id")}
            {...form.getInputProps("method_id")}
          />
          <PreservationStatusSelectBox
            key={form.key("status_id")}
            {...form.getInputProps("status_id")}
          />

          <ContactSelector
            query={{ case_id: defaultFormData?.case_id }}
            size="sm"
            variant="outlined"
            clearable={true}
            allowCreateNew={true}
            onContactCreated={(contact) => {
              form.setFieldValue("contact_id", contact.client_id);
              form.setFieldValue("linked_contact", {
                value: contact.client_id,
                label: contact.name,
                data: contact,
              });
            }}
            description="The contact that is associated with this preservation"
            key={form.key("linked_contact")}
            {...form.getInputProps("linked_contact")}
            onChange={(_, option) => {
              form.setFieldValue("linked_contact", option);
              form.setFieldValue("contact_id", option?.value);
            }}
          />
          <EvidenceSelector
            size="sm"
            clearable
            label="Evidence"
            query={{
              case_id: defaultFormData?.case_id,
            }}
            placeholder="Select Evidence..."
            description="This is the evidence that is associated with this preservation."
            key={form.key("linked_evidence")}
            {...form.getInputProps("linked_evidence")}
            onChange={(_, option) => {
              form.setFieldValue("linked_evidence", option);
              form.setFieldValue("evidence_id", option?.value);
            }}
          />
          <DateInput
            size="sm"
            variant="outlined"
            label="Start Date"
            description="Starting date of data to be preserved"
            clearable={true}
            arrow={true}
            enableCalendar={true}
            format={
              getDateFormat({
                isMoment: true,
                includeTime: false,
              }) as DateInputFormat
            }
            key={form.key("start_date")}
            {...form.getInputProps("start_date")}
          />
          <DateInput
            size="sm"
            variant="outlined"
            label="End Date"
            description="Ending date of data to be preserved"
            clearable={true}
            arrow={true}
            enableCalendar={true}
            format={
              getDateFormat({
                isMoment: true,
                includeTime: false,
              }) as DateInputFormat
            }
            key={form.key("end_date")}
            {...form.getInputProps("end_date")}
          />
          <TextAreaInput
            colSpan={2}
            minRows={8}
            maxRows={24}
            size="sm"
            variant="outlined"
            label="Description"
            placeholder="Enter preservation description"
            description="The description of the preservation"
            key={form.key("description")}
            {...form.getInputProps("description")}
          />
        </Grid>
        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            variant="contained"
            onClick={handleSubmit}
            disabled={isPending}
            loading={isPending}
          >
            Submit
          </Button>
        </ButtonMenu>
      </FormContainer>
    );
  };
